import { useState, useCallback, useEffect, useMemo } from 'react';
import supabase from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const CACHE_KEY = 'dms_search_users';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const getCache = (userRole, userId) => {
  try {
    const cacheKey = `${CACHE_KEY}_${userRole}_${userId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

const setCache = (data, userRole, userId) => {
  try {
    const cacheKey = `${CACHE_KEY}_${userRole}_${userId}`;
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
  }
};

export const useSearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const { user, userRole } = useAuth();

  // Fetch all users if cache is empty or on real-time update
  useEffect(() => {
    const fetchAllUsers = async () => {
      // Check cache first
      const cachedData = getCache(userRole, user?.id);
      if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
        setAllUsers(cachedData);
        setLoading(false);
        return;
      }
      // Clear invalid cache
      if (cachedData) {
        localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
      }

      try {
        if (userRole === 'teacher') {
          // Get teacher's classes
          const { data: teacherClasses, error: classesError } = await supabase
            .from('classes')
            .select('id')
            .eq('teacher_id', user.id);

          if (classesError) throw classesError;

          const classIds = teacherClasses.map(c => c.id);

          // Get all teachers for the teacher to see
          const { data: allTeachers, error: teachersError } = await supabase
            .from('teachers')
            .select('*');

          if (teachersError) throw teachersError;

          // Get students in teacher's classes with their guardians
          const { data: students, error: studentsError } = await supabase
            .from('students')
            .select(`
              id,
              first_name,
              last_name,
              class_ids,
              is_self_guardian,
              student_guardians(
                guardian_id,
                is_primary,
                relationship_type,
                guardians(*)
              )
            `)
            .eq('is_active', true)
            .overlaps('class_ids', classIds);

          if (studentsError) throw studentsError;

          // Get all guardians from student relationships
          const allGuardians = (students || [])
            .flatMap(s => s.student_guardians || [])
            .map(sg => sg.guardians)
            .filter(Boolean);

          // Filter guardians to keep only one instance of each ID
          const guardians = allGuardians.filter((guardian, index, self) => 
            index === self.findIndex(g => g.id === guardian.id)
          );

          // Format results
          const formattedUsers = [
            ...(allTeachers || []).map(teacher => ({
              id: teacher.id,
              label: `${teacher.first_name} ${teacher.last_name} (Teacher)`,
              role: 'teacher',
              firstName: teacher.first_name.toLowerCase(),
              lastName: teacher.last_name.toLowerCase(),
              email: (teacher.email || '').toLowerCase()
            })),
            ...(students || []).map(student => ({
              id: student.id,
              label: student.is_self_guardian 
                ? `${student.first_name} ${student.last_name} (Student - Self Guardian)`
                : `${student.first_name} ${student.last_name} (Student)`,
              role: 'student',
              firstName: student.first_name.toLowerCase(),
              lastName: student.last_name.toLowerCase()
            })),
            ...guardians.map(guardian => ({
              id: guardian.id,
              label: `${guardian.first_name} ${guardian.last_name} (Guardian)`,
              role: 'guardian',
              firstName: guardian.first_name.toLowerCase(),
              lastName: guardian.last_name.toLowerCase(),
              email: (guardian.email || '').toLowerCase()
            }))
          ];

          setAllUsers(formattedUsers);
          setCache(formattedUsers, userRole, user.id);
        } else {
          // Admin/Superadmin fetch all users
          const [
            { data: students, error: studentsError },
            { data: teachers, error: teachersError },
            { data: guardians, error: guardiansError }
          ] = await Promise.all([
            supabase
              .from('students')
              .select(`
                id, 
                first_name, 
                last_name,
                is_self_guardian,
                student_guardians(
                  guardian_id,
                  is_primary,
                  relationship_type,
                  guardians(*)
                )
              `)
              .eq('is_active', true),
            supabase
              .from('teachers')
              .select('*')
              .eq('is_active', true),
            supabase
              .from('guardians')
              .select('*')
              .eq('is_active', true)
          ]);

          if (studentsError) throw studentsError;
          if (teachersError) throw teachersError;
          if (guardiansError) throw guardiansError;

          // Get all guardians from both sources
          const allGuardians = [
            ...(guardians || []),
            ...(students || [])
              .flatMap(s => s.student_guardians || [])
              .map(sg => sg.guardians)
              .filter(Boolean)
          ];

          // Filter guardians to keep only one instance of each ID
          const uniqueGuardians = allGuardians.filter((guardian, index, self) => 
            index === self.findIndex(g => g.id === guardian.id)
          );

          const formattedUsers = [
            ...(students?.map(student => ({
              id: student.id,
              label: student.is_self_guardian 
                ? `${student.first_name} ${student.last_name} (Student - Self Guardian)`
                : `${student.first_name} ${student.last_name} (Student)`,
              role: 'student',
              firstName: student.first_name.toLowerCase(),
              lastName: student.last_name.toLowerCase(),
              is_self_guardian: student.is_self_guardian
            })) || []),
            ...(teachers?.map(teacher => ({
              id: teacher.id,
              label: `${teacher.first_name} ${teacher.last_name} (Teacher)`,
              role: 'teacher',
              firstName: teacher.first_name.toLowerCase(),
              lastName: teacher.last_name.toLowerCase(),
              email: (teacher.email || '').toLowerCase()
            })) || []),
            ...uniqueGuardians.map(guardian => ({
              id: guardian.id,
              label: `${guardian.first_name} ${guardian.last_name} (Guardian)`,
              role: 'guardian',
              firstName: guardian.first_name.toLowerCase(),
              lastName: guardian.last_name.toLowerCase(),
              email: (guardian.email || '').toLowerCase()
            }))
          ];

          setAllUsers(formattedUsers);
          setCache(formattedUsers, userRole, user.id);
        }

        setError(null);
      } catch (err) {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();

    // Set up real-time subscriptions
    const subscription = supabase
      .channel('db_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'students' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'teachers' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'guardians' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'student_guardians' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'classes' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .on('broadcast',
        { event: 'guardian_update' },
        () => {
          localStorage.removeItem(`${CACHE_KEY}_${userRole}_${user?.id}`);
          setLastUpdate(Date.now());
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [lastUpdate, userRole, user?.id]);

  const searchUsers = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const terms = searchTerm.toLowerCase().split(' ');
    const filtered = allUsers.filter(user => {
      return terms.every(term => 
        user.firstName.includes(term) ||
        user.lastName.includes(term) ||
        (user.email && user.email.includes(term))
      );
    }).slice(0, 20);

    setSearchResults(filtered);
  }, [allUsers]);

  const counts = useMemo(() => ({
    students: allUsers.filter(u => u.role === 'student').length,
    teachers: allUsers.filter(u => u.role === 'teacher').length,
    guardians: allUsers.filter(u => u.role === 'guardian').length,
    total: allUsers.length
  }), [allUsers]);

  return {
    searchResults,
    loading,
    searchUsers,
    error,
    counts
  };
}; 